// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { logout as authServiceLogout, getUserProfile } from '../services/authService';


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load auth state from localStorage when app starts
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
        if (!parsedUser.name) hydrateUserName();
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  // The login response only carries { email, role } - the display name lives
  // on the profile endpoint, so fetch it once in the background and merge it
  // in wherever the navbar and other "who's logged in" UI read `user`.
  const hydrateUserName = async () => {
    try {
      const profile = await getUserProfile();
      updateUser({ name: profile.name });
    } catch (error) {
      // Best-effort: UI falls back to email if this fails.
    }
  };

  const updateUser = (partial) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const login = (loginResponse) => {
    apiClient.setTokens(loginResponse.accessToken, loginResponse.refreshToken);

    const userData = {
      email: loginResponse.email,
      role: loginResponse.role
    };

    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    hydrateUserName();
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    // Best-effort: revoke the refresh token server-side and clear local storage.
    // Not awaited - the UI should update immediately regardless of network state.
    authServiceLogout();
  };

  const isAdmin = () => {
    return user && user.role === 'ADMIN';
  };

  const isModerator = () => {
    return user && user.role === 'MODERATOR';
  };

  const isUser = () => {
    return user && user.role === 'USER';
  };

  const getToken = () => {
    return localStorage.getItem('accessToken');
  };

  const getRefreshToken = () => {
    return localStorage.getItem('refreshToken');
  };

  const updateTokens = (accessToken, refreshToken) => {
    apiClient.setTokens(accessToken, refreshToken);
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      login,
      logout,
      isAdmin,
      isModerator,
      isUser,
      getToken,
      getRefreshToken,
      updateTokens,
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context easily
export const useAuth = () => {
  return useContext(AuthContext);
};
